// FILE: src/actions/actions_helper.js
// FIX: Keep backward compatibility by dispatching data.data by default (old behaviour)
// FIX: Add optional pass_full_response flag for endpoints like Stock Levels that need full response object
// FIX: Prevent "stuck on LOADING" by failing when successMessage mismatch (only if successMessage is provided)
// FIX: Added detailed backend error logging for easier debugging

import {
  makeClientApiRequest,
  makeUnAuthApiRequest,
  makeEmployeeDashboardApiRequest,
  authSocketApiConnectionHelper,
} from "../api/api_helper";

import { success_sign_out_modal } from "../reducers/dashboard_reducers/auth/sign_out_modal_Slice";
import { messageToast, rSelectMapFun } from "../utils/Utils";
import * as app_consts from "../constants/react_app_consts";
import { map_system_actions, retreive_rSelectVariables } from "../utils/functionalUtils";
import { reset_get_current_user_data, success_get_current_user_data } from "../reducers/dashboard_reducers/auth/get_current_user_data_Slice";

const extractApiErrorMessage = (error, fallbackMessage) => {
  const responseData = error?.response?.data;

  if (responseData?.message)
  {
    return responseData.message;
  }

  if (responseData?.errors?.detail)
  {
    return responseData.errors.detail;
  }

  if (responseData?.errors && typeof responseData.errors === "object")
  {
    const firstErrorValue = Object.values(responseData.errors)[0];

    if (Array.isArray(firstErrorValue) && firstErrorValue.length > 0)
    {
      return firstErrorValue[0];
    }

    if (typeof firstErrorValue === "string" && firstErrorValue.trim())
    {
      return firstErrorValue;
    }
  }

  return fallbackMessage;
};

const dispatchActionCreator = (dispatch, actionCreator, payload, actionName) => {
  if (typeof actionCreator !== "function")
  {
    console.error(`${actionName} is not a function`, actionCreator);
    return;
  }

  dispatch(actionCreator(payload));
};

// ------------------------------------- CLIENT ACTIONS HELPER -------------------------------------
export const client_actions_helper =
  (
    loadingState,
    successState,
    failState,
    errorMessage,
    successMessage,
    apiReqType,
    apiEndpoint,
    formData = null,
    accessDeniedMessage = null
  ) =>
    async (dispatch) => {
      try
      {
        dispatchActionCreator(dispatch, loadingState, undefined, "loadingState");

        const { data } = await makeClientApiRequest(apiReqType, apiEndpoint, formData);

        // If successMessage provided, validate it
        const messageMatches = !successMessage || data?.message === successMessage;

        if (messageMatches)
        {
          // Keep old behaviour: pass payload as data.data
          dispatchActionCreator(dispatch, successState, data?.data, "successState");
        } else
        {
          dispatchActionCreator(dispatch, failState, data?.message || errorMessage, "failState");
        }
      } catch (error)
      {
        console.log(error);
        console.error("CLIENT API ERROR STATUS =>", error?.response?.status);
        console.error("CLIENT API ERROR DATA =>", error?.response?.data);
        console.error("CLIENT API ERROR FULL =>", error?.response);

        if (error.code === "ERR_NETWORK" || error.code === "ERR_BAD_RESPONSE")
        {
          dispatchActionCreator(dispatch, failState, errorMessage, "failState");
          return;
        }

        const status = error?.response?.status;
        const serverMsg = extractApiErrorMessage(error, errorMessage);

        if (status === 400 || status === 404 || status === 403)
        {
          dispatchActionCreator(dispatch, failState, serverMsg || errorMessage, "failState");
        } else if (status === 401)
        {
          dispatch(success_sign_out_modal());
        } else if (status === 404)
        {
          messageToast(accessDeniedMessage, app_consts.FAIL);
          dispatchActionCreator(dispatch, failState, accessDeniedMessage, "failState");
        } else
        {
          dispatchActionCreator(dispatch, failState, serverMsg || errorMessage, "failState");
        }
      }
    };

// ------------------------------------- EMPLOYEE DASHBOARD ACTIONS HELPER -------------------------------------
export const employee_dashboard_actions_helper =
  (
    loadingState,
    successState,
    failState,
    errorMessage,
    successMessage, // can be null/"" if you don't want strict message validation
    apiReqType,
    apiEndpoint,
    formData = null,
    accessDeniedMessage = null,
    res_action = null,
    res_type = null,
    pass_full_response = false // ✅ NEW: default false (backward compatible)
  ) =>
    async (dispatch) => {
      try
      {
        dispatchActionCreator(dispatch, loadingState, undefined, "loadingState");

        const { data } = await makeEmployeeDashboardApiRequest(apiReqType, apiEndpoint, formData);

        // Only validate successMessage if provided
        const messageMatches = !successMessage || data?.message === successMessage;

        if (!messageMatches)
        {
          dispatchActionCreator(dispatch, failState, data?.message || errorMessage, "failState");
          return;
        }

        // Keep existing mapping behaviour
        if (res_action === app_consts.MAP)
        {
          if (res_type === app_consts.AUTH_ROUTES_MAP)
          {
            let mappedData = {};

            await Promise.all(
              (data?.data || [])?.map(async (data_to_map) => {
                const mappedResult = await map_system_actions(data_to_map);
                mappedData[data_to_map.module_name] = mappedResult;
              })
            );

            dispatchActionCreator(dispatch, successState, mappedData, "successState");
            return;
          } else
          {
            let mappedData = [];

            await Promise.all(
              (data?.data || [])?.map(async (data_to_map) => {
                const { label, value } = retreive_rSelectVariables(res_type, data_to_map);
                const result = await rSelectMapFun(label, value);
                mappedData.push(result);
              })
            );

            dispatchActionCreator(dispatch, successState, mappedData, "successState");
            return;
          }
        }

        /**
         * IMPORTANT FIX:
         * - Old pages expect payload to be data.data (array/list/object they actually render)
         * - Special pages can set pass_full_response=true if they need full {message,data}
         */
        const payload = pass_full_response ? data : data?.data;
        dispatchActionCreator(dispatch, successState, payload, "successState");
      } catch (error)
      {
        console.log(error);
        console.error("EMPLOYEE API ERROR STATUS =>", error?.response?.status);
        console.error("EMPLOYEE API ERROR DATA =>", error?.response?.data);
        console.error("EMPLOYEE API ERROR FULL =>", error?.response);
        console.error(
          "EMPLOYEE API ERROR DATA JSON =>",
          JSON.stringify(error?.response?.data, null, 2)
        ); console.error("EMPLOYEE API ENDPOINT =>", apiEndpoint);
        console.error("EMPLOYEE API METHOD =>", apiReqType);

        if (error.code === "ERR_NETWORK" || error.code === "ERR_BAD_RESPONSE")
        {
          dispatchActionCreator(dispatch, failState, errorMessage, "failState");
          return;
        }

        const status = error?.response?.status;
        const serverMsg = extractApiErrorMessage(error, errorMessage);

        if (status === 400 || status === 404)
        {
          dispatchActionCreator(dispatch, failState, serverMsg || errorMessage, "failState");
          return;
        }

        if (status === 401)
        {
          console.log(serverMsg);
          if ((serverMsg || "").trim() === "Unauthenticated")
          {
            dispatchActionCreator(dispatch, failState, serverMsg || "Unauthenticated", "failState");
            dispatch(
              success_sign_out_modal({
                loadingState,
                successState,
                failState,
                errorMessage,
                successMessage,
                apiReqType,
                apiEndpoint,
                formData,
              })
            );
          } else
          {
            dispatchActionCreator(dispatch, failState, serverMsg || "Unauthorized", "failState");
          }
          return;
        }

        if (status === 403)
        {
          messageToast(accessDeniedMessage, app_consts.FAIL);
          dispatchActionCreator(dispatch, failState, accessDeniedMessage, "failState");
          return;
        }

        dispatchActionCreator(dispatch, failState, serverMsg || errorMessage, "failState");
      }
    };

// ------------------------------------- WEBSITE ACTIONS HELPER -------------------------------------
export const website_actions_helper =
  (
    loadingState,
    successState,
    failState,
    errorMessage,
    successMessage,
    apiReqType,
    apiEndpoint,
    formData = null
  ) =>
    async (dispatch) => {
      try
      {
        dispatchActionCreator(dispatch, loadingState, undefined, "loadingState");

        const { data } = await makeUnAuthApiRequest(apiReqType, apiEndpoint, formData);

        const messageMatches = !successMessage || data?.message === successMessage;

        if (messageMatches)
        {
          dispatchActionCreator(dispatch, successState, data?.data, "successState");
        } else
        {
          dispatchActionCreator(dispatch, failState, data?.message || errorMessage, "failState");
        }
      } catch (error)
      {
        console.log(error);
        console.error("WEBSITE API ERROR STATUS =>", error?.response?.status);
        console.error("WEBSITE API ERROR DATA =>", error?.response?.data);
        console.error("WEBSITE API ERROR FULL =>", error?.response);

        if (error.code === "ERR_NETWORK" || error.code === "ERR_BAD_RESPONSE")
        {
          dispatchActionCreator(dispatch, failState, errorMessage, "failState");
          return;
        }

        const status = error?.response?.status;
        const serverMsg = extractApiErrorMessage(error, errorMessage);

        if (status === 400)
        {
          dispatchActionCreator(dispatch, failState, serverMsg || errorMessage, "failState");
        } else
        {
          dispatchActionCreator(dispatch, failState, serverMsg || errorMessage, "failState");
        }
      }
    };

// Channels Action Helpers
export const channel_actions_helper =
  (connection) =>
    async (dispatch) => {
      try
      {
        if (!connection)
        {
          console.warn("⚠️ No socket connection provided.");
          return null;
        }

        // Retrieve Socket Connection
        const socket_connection = await authSocketApiConnectionHelper();

        if (!socket_connection)
        {
          console.warn("⚠️ Socket Not Available.");
          return null;
        }

        // Market Marquee Data Chanel
        const current_account_channel = socket_connection.channel("room:current_account_data", {});

        // ✅ register first
        current_account_channel.on("current_account_update", (payload) => {
          dispatch(success_get_current_user_data(payload?.current_account));
        });

        // then join
        current_account_channel
          .join()
          .receive("ok", () => {
            console.log("✅ Joined current_account channel");
          })
          .receive("error", (resp) => {
            dispatch(reset_get_current_user_data());
            console.error("❌ Unable to join Current Account", resp);
          });

      } catch (err)
      {
        console.error("❌ Socket connection error:", err);
        dispatch({ type: "SOCKET_ERROR", payload: err.message });
        return null;
      }
    };
